import { snakeCase } from 'change-case';
import type { StateCreator } from 'zustand';

import type { DateRangeFns, Sort } from '@cobliteam/common-types';

import {
  type IFiltersDef,
  type SortingState,
  type TFilterKeys,
  filterGetParamDates,
  filterGetParamFilters,
  filterSetParamDates,
  filterSetParamFilters,
} from '@cobliteam/ui-business/components/DataTable';

import { endOfDay, startOfDay } from '@cobliteam/utils/date';
import { EventTableType } from '../../../utils';

export interface MediaEventSliceState {
  mediaEventDates: DateRangeFns | null;
  mediaEventFilters: IFiltersDef[];
  mediaEventSort: Sort | undefined;
  mediaEventTab: EventTableType;
  setMediaEventDates: (dates: DateRangeFns) => void;
  setMediaEventFilters: (filter: IFiltersDef) => void;
  setMediaEventSort: (sorts: SortingState) => void;
  getFilterValueByKey: <T>(key: TFilterKeys) => T | undefined;
  setMediaEventTab: (tab: EventTableType) => void;
  clearMediaEventFilters: () => void;
}

type StateCreatormediaEventTable = StateCreator<MediaEventSliceState, [['zustand/devtools', never]], [], MediaEventSliceState>;

const mediaEventSlice: StateCreatormediaEventTable = (set, get) => ({
  mediaEventDates: filterGetParamDates(),
  mediaEventFilters: filterGetParamFilters(),
  mediaEventSort: undefined,
  mediaEventTab: EventTableType.EVENT_MEDIA,
  setMediaEventDates: (dates: DateRangeFns) =>
    set(
      (): Partial<MediaEventSliceState> => {
        filterSetParamDates({
          startDate: startOfDay(dates.startDate),
          endDate: endOfDay(dates.endDate),
        });
        return { mediaEventDates: dates };
      },
      false,
      'setDates'
    ),
  setMediaEventFilters: (filter: IFiltersDef) =>
    set(
      (state): Partial<MediaEventSliceState> => {
        const filterDef = state.mediaEventFilters.find((f: IFiltersDef) => f.key === filter.key) as IFiltersDef;
        const newFilter = {
          ...filterDef,
          ...filter,
        };
        filterSetParamFilters(newFilter);

        // create a copy of the filters array
        const updatedFilters = [...state.mediaEventFilters];

        // find the filter with the matching key and update its value
        const filterIndex = updatedFilters.findIndex((f) => f.key === filter.key);

        if (filterIndex !== -1) {
          updatedFilters[filterIndex].value = filter.value;
        }

        return { mediaEventFilters: updatedFilters };
      },
      false,
      'setFilters'
    ),
  setMediaEventSort: (sorts: SortingState) =>
    set(
      (): Partial<MediaEventSliceState> => {
        const newSort = sorts.length
          ? {
              column: snakeCase(sorts[0].id),
              order: sorts[0].desc ? 'desc' : 'asc',
            }
          : undefined;

        return {
          mediaEventSort: newSort as Sort,
        };
      },
      false,
      'setSort'
    ),
  getFilterValueByKey: (key: TFilterKeys) => get().mediaEventFilters.find((f: IFiltersDef) => f.key === key)?.value,
  setMediaEventTab: (tab: EventTableType) =>
    set(() => ({
      mediaEventTab: tab,
    })),
  clearMediaEventFilters: () =>
    set(
      (state): Partial<MediaEventSliceState> => {
        const filtersToClearKeys = [
          'MultiSelectMediaEventTrigger',
          'MultiSelectMediaSeverity',
          'MultiSelectVehicles',
          'MultiSelectVehicleGroups',
          'MultiSelectDrivers',
          'MultiSelectMediaEventStatus',
        ];

        const updatedFilters = [...state.mediaEventFilters];

        filtersToClearKeys.forEach((key) => {
          const filterIndex = updatedFilters.findIndex((f) => f.key === key);

          if (filterIndex !== -1 && updatedFilters[filterIndex].value.length > 0) {
            updatedFilters[filterIndex].value = [];

            filterSetParamFilters(updatedFilters[filterIndex]);
          }
        });

        return { mediaEventFilters: updatedFilters };
      },
      false,
      'clearSelectedFilters'
    ),
});

export default mediaEventSlice;
